import React from 'react';
import { Box, Container, Typography } from '@mui/material';
import qrPy from '../../../../assets/qrRealConsulting.png';
import minturPy from '../../../../assets/minturPyColor.svg';
import logo from '../../../../assets/TiendaNaranjaViajesLogo.svg';
import itauNewLogo from '../../../../assets/itau-logo.png';
import mevueloLogo from '../../../../assets/logo_normal.svg';

import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import EmailIcon from '@mui/icons-material/Email';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';

/**
 * Footer de Tienda Naranja Viajes (co-brand Itaú + Mevuelo).
 * Misma estructura que los footers rediseñados, pero con la paleta clara de la
 * marca (fondo blanco, gris #595957, acento naranja Itaú) en vez del violeta.
 */

const TN_TEXT = '#595957';
const TN_ACCENT = '#EE7000';
const TN_BORDER = 'rgba(89, 89, 87, 0.16)';
const TN_SOFT_BG = 'rgba(89, 89, 87, 0.05)';

const contactItems = [
	{
		label: '021 729 0 629',
		subtitle: 'opción 2',
		href: 'tel:0217290629',
		icon: <LocalPhoneIcon />,
	},
	{
		label: 'viajes@tiendanaranja.com.py',
		href: 'mailto:viajes@tiendanaranja.com.py',
		icon: <EmailIcon />,
	},
];

const socialItems = [
	{
		label: 'Facebook',
		value: 'Tienda Naranja Viajes PY',
		href: 'https://www.facebook.com/p/Tienda-Naranja-Viajes-PY-100094724016374/',
		icon: <FacebookIcon />,
	},
	{
		label: 'Instagram',
		value: 'tiendanaranjaviajespy',
		href: 'https://www.instagram.com/tiendanaranjaviajespy/',
		icon: <InstagramIcon />,
	},
	{
		label: 'WhatsApp',
		value: '984 729 300',
		href: 'http://wa.me/595984729300',
		icon: <WhatsAppIcon />,
	},
];

const linkSx = {
	color: 'inherit',
	textDecoration: 'none',
};

const TiendaNaranjaPyFooter = (): JSX.Element => {
	return (
		<Box component="footer" sx={{ mt: 4 }}>
			<Container sx={{ px: { xs: 1, sm: 2 } }}>
				<Box
					sx={{
						position: 'relative',
						overflow: 'hidden',
						borderRadius: { xs: '16px 16px 0 0', sm: '16px' },
						backgroundColor: '#FFFFFF',
						color: TN_TEXT,
						px: { xs: 2, sm: 4 },
						pt: { xs: 3, sm: 4 },
						pb: { xs: 3, sm: 4 },
						border: `1px solid ${TN_BORDER}`,
						boxShadow: '0 -10px 30px rgba(89, 89, 87, 0.12)',
					}}
				>
					<Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={3}>
						<Box>
							<Typography variant="overline" sx={{ color: 'rgba(89,89,87,0.7)', fontWeight: 800 }}>
								Estamos para ayudarte
							</Typography>
							<Typography variant="h6" fontWeight={800} lineHeight={1.15}>
								Contactá con Tienda Naranja Viajes
							</Typography>
						</Box>
						<img src={logo} alt="Tienda Naranja Viajes" style={{ width: 120, maxWidth: '30vw' }} />
					</Box>

					<Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)' }} gap={1}>
						{contactItems.map((item) => (
							<a key={item.label} style={linkSx} href={item.href} rel="noreferrer">
								<Box
									display="flex"
									alignItems="center"
									gap={1}
									sx={{
										height: '100%',
										p: 1.25,
										borderRadius: '10px',
										backgroundColor: TN_SOFT_BG,
										border: `1px solid ${TN_BORDER}`,
									}}
								>
									<Box sx={{ flex: '0 0 auto', '& svg': { fontSize: 20, color: TN_ACCENT, display: 'block' } }}>
										{item.icon}
									</Box>
									<Box minWidth={0}>
										<Typography variant="body2" fontWeight={800} lineHeight={1.2} sx={{ overflowWrap: 'anywhere' }}>
											{item.label}
										</Typography>
										{item.subtitle && (
											<Typography
												variant="caption"
												display="block"
												sx={{ color: 'rgba(89,89,87,0.72)', overflowWrap: 'anywhere' }}
											>
												{item.subtitle}
											</Typography>
										)}
									</Box>
								</Box>
							</a>
						))}
					</Box>

					<Box mt={2.5}>
						<Typography variant="body2" fontWeight={800} mb={1}>
							Seguinos
						</Typography>
						<Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: 'repeat(3, 1fr)' }} gap={1}>
							{socialItems.map((item) => (
								<a key={item.label} style={linkSx} href={item.href} target="_blank" rel="noreferrer">
									<Box
										display="flex"
										alignItems="center"
										gap={1}
										sx={{
											p: 1,
											borderRadius: '999px',
											backgroundColor: TN_SOFT_BG,
											border: `1px solid ${TN_BORDER}`,
											'& svg': { fontSize: 20, color: TN_ACCENT },
										}}
									>
										{item.icon}
										<Box minWidth={0}>
											<Typography
												variant="caption"
												display="block"
												lineHeight={1}
												sx={{ color: 'rgba(89,89,87,0.68)' }}
											>
												{item.label}
											</Typography>
											<Typography variant="caption" fontWeight={800} sx={{ overflowWrap: 'anywhere' }}>
												{item.value}
											</Typography>
										</Box>
									</Box>
								</a>
							))}
						</Box>
					</Box>

					<Box
						mt={2.5}
						sx={{
							p: 1.5,
							borderRadius: '12px',
							backgroundColor: TN_SOFT_BG,
							border: `1px solid ${TN_BORDER}`,
						}}
					>
						<Box display="flex" alignItems="center" gap={1} mb={1}>
							<VerifiedOutlinedIcon sx={{ color: TN_ACCENT }} />
							<Typography variant="body2" fontWeight={800}>
								Agencia de viajes registrada
							</Typography>
						</Box>
						<Box
							display="grid"
							gridTemplateColumns={{ xs: '1fr auto', sm: '1fr auto auto' }}
							gap={1.5}
							alignItems="center"
						>
							<Box>
								<Typography variant="body2" fontWeight={800}>
									REAL CONSULTING S.A.
								</Typography>
								<Typography variant="caption" sx={{ color: 'rgba(89,89,87,0.72)' }}>
									Resolución REGISTUR Nº 1263/2019
								</Typography>
							</Box>
							<a
								style={linkSx}
								href="https://registur.gov.py/registur/verificacion?qr=e4e4f7c8442ad196282fb79b77bd6abe"
								target="_blank"
								rel="noreferrer"
							>
								<img src={qrPy} alt="QR registro REGISTUR" style={{ width: 76, borderRadius: 8, display: 'block' }} />
							</a>
							<Box display={{ xs: 'none', sm: 'block' }}>
								<img src={minturPy} alt="SENATUR Paraguay" style={{ width: 92, display: 'block' }} />
							</Box>
						</Box>
					</Box>

					{/* Pie discreto: los logos reemplazan a los nombres en texto. */}
					<Box display="flex" alignItems="center" justifyContent="center" gap={0.75} mt={2.5} flexWrap="wrap">
						<Typography variant="caption" sx={{ color: 'rgba(89,89,87,0.78)' }}>
							Tienda Naranja Viajes es un proyecto de
						</Typography>
						<a
							style={{ ...linkSx, display: 'inline-flex' }}
							href="https://www.itau.com.py"
							target="_blank"
							rel="noreferrer"
						>
							<img src={itauNewLogo} alt="Itaú" style={{ height: 18, borderRadius: 4, display: 'block' }} />
						</a>
						<Typography variant="caption" sx={{ color: 'rgba(89,89,87,0.78)' }}>
							y
						</Typography>
						<a
							style={{ ...linkSx, display: 'inline-flex' }}
							href="https://www.mevuelo.com.py"
							target="_blank"
							rel="noreferrer"
						>
							<img src={mevueloLogo} alt="Mevuelo" style={{ height: 16, display: 'block' }} />
						</a>
					</Box>
				</Box>
			</Container>
		</Box>
	);
};
export default TiendaNaranjaPyFooter;
